// =============================================================================
// BP-05-03: Calendar Frontend - RecurrenceEditor Component
// =============================================================================

import React, { useState, useEffect } from 'react';
import { format, addMonths, addYears } from 'date-fns';
import { Calendar, Repeat, X } from 'lucide-react';
import { RecurrenceFrequency } from '../types';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface RecurrenceConfig {
  frequency: RecurrenceFrequency;
  interval: number;
  byDay: string[];
  byMonthDay: number[];
  endType: 'never' | 'after' | 'on';
  endCount?: number;
  endDate?: string;
}

interface RecurrenceEditorProps {
  value: string; // RRULE string
  onChange: (rrule: string) => void;
  startDate: Date;
  className?: string;
}

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const DAYS_OF_WEEK = [
  { short: 'SU', long: 'Sunday' },
  { short: 'MO', long: 'Monday' },
  { short: 'TU', long: 'Tuesday' },
  { short: 'WE', long: 'Wednesday' },
  { short: 'TH', long: 'Thursday' },
  { short: 'FR', long: 'Friday' },
  { short: 'SA', long: 'Saturday' },
];

const FREQUENCY_OPTIONS = [
  { value: RecurrenceFrequency.DAILY, label: 'Daily' },
  { value: RecurrenceFrequency.WEEKLY, label: 'Weekly' },
  { value: RecurrenceFrequency.MONTHLY, label: 'Monthly' },
  { value: RecurrenceFrequency.YEARLY, label: 'Yearly' },
];

const INTERVAL_LABELS: Record<RecurrenceFrequency, string> = {
  [RecurrenceFrequency.DAILY]: 'day',
  [RecurrenceFrequency.WEEKLY]: 'week',
  [RecurrenceFrequency.MONTHLY]: 'month',
  [RecurrenceFrequency.YEARLY]: 'year',
};

// -----------------------------------------------------------------------------
// Utilities
// -----------------------------------------------------------------------------

function parseRRule(rrule: string): RecurrenceConfig {
  const config: RecurrenceConfig = {
    frequency: RecurrenceFrequency.WEEKLY,
    interval: 1,
    byDay: [],
    byMonthDay: [],
    endType: 'never',
  };

  if (!rrule) return config;

  // Parse FREQ
  const freqMatch = rrule.match(/FREQ=(\w+)/);
  if (freqMatch) {
    const freq = freqMatch[1].toLowerCase();
    if (freq === 'daily') config.frequency = RecurrenceFrequency.DAILY;
    else if (freq === 'weekly') config.frequency = RecurrenceFrequency.WEEKLY;
    else if (freq === 'monthly') config.frequency = RecurrenceFrequency.MONTHLY;
    else if (freq === 'yearly') config.frequency = RecurrenceFrequency.YEARLY;
  }

  // Parse INTERVAL
  const intervalMatch = rrule.match(/INTERVAL=(\d+)/);
  if (intervalMatch) {
    config.interval = parseInt(intervalMatch[1], 10);
  }

  // Parse BYDAY
  const byDayMatch = rrule.match(/BYDAY=([A-Z,]+)/);
  if (byDayMatch) {
    config.byDay = byDayMatch[1].split(',');
  }

  // Parse BYMONTHDAY
  const byMonthDayMatch = rrule.match(/BYMONTHDAY=(\d+)/);
  if (byMonthDayMatch) {
    config.byMonthDay = [parseInt(byMonthDayMatch[1], 10)];
  }

  // Parse COUNT
  const countMatch = rrule.match(/COUNT=(\d+)/);
  if (countMatch) {
    config.endType = 'after';
    config.endCount = parseInt(countMatch[1], 10);
  }

  // Parse UNTIL
  const untilMatch = rrule.match(/UNTIL=(\d{8})/);
  if (untilMatch) {
    config.endType = 'on';
    const dateStr = untilMatch[1];
    config.endDate = `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
  }

  return config;
}

function generateRRule(config: RecurrenceConfig): string {
  const parts: string[] = [
    `FREQ=${config.frequency.toUpperCase()}`,
  ];

  if (config.interval > 1) {
    parts.push(`INTERVAL=${config.interval}`);
  }

  if (config.frequency === RecurrenceFrequency.WEEKLY && config.byDay.length > 0) {
    parts.push(`BYDAY=${config.byDay.join(',')}`);
  }

  if (config.frequency === RecurrenceFrequency.MONTHLY && config.byMonthDay.length > 0) {
    parts.push(`BYMONTHDAY=${config.byMonthDay[0]}`);
  }

  if (config.endType === 'after' && config.endCount) {
    parts.push(`COUNT=${config.endCount}`);
  }

  if (config.endType === 'on' && config.endDate) {
    const date = config.endDate.replace(/-/g, '');
    parts.push(`UNTIL=${date}`);
  }

  return parts.join(';');
}

function describeRRule(config: RecurrenceConfig, startDate: Date): string {
  const intervalLabel = INTERVAL_LABELS[config.frequency];
  const intervalText =
    config.interval === 1
      ? `Every ${intervalLabel}`
      : `Every ${config.interval} ${intervalLabel}s`;

  let description = intervalText;

  if (config.frequency === RecurrenceFrequency.WEEKLY && config.byDay.length > 0) {
    const dayNames = config.byDay.map((d) =>
      DAYS_OF_WEEK.find((day) => day.short === d)?.long || d
    );
    description += ` on ${dayNames.join(', ')}`;
  }

  if (config.frequency === RecurrenceFrequency.MONTHLY) {
    const dayOfMonth = config.byMonthDay.length > 0 ? config.byMonthDay[0] : startDate.getDate();
    description += ` on day ${dayOfMonth}`;
  }

  if (config.endType === 'after' && config.endCount) {
    description += `, ${config.endCount} times`;
  } else if (config.endType === 'on' && config.endDate) {
    description += `, until ${format(new Date(config.endDate), 'MMM d, yyyy')}`;
  }

  return description;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function RecurrenceEditor({
  value,
  onChange,
  startDate,
  className,
}: RecurrenceEditorProps) {
  const [config, setConfig] = useState<RecurrenceConfig>(() => parseRRule(value));

  // Update parent when config changes
  useEffect(() => {
    const rrule = generateRRule(config);
    if (rrule !== value) {
      onChange(rrule);
    }
  }, [config, onChange, value]);

  // Set default byDay based on startDate if weekly
  useEffect(() => {
    if (config.frequency === RecurrenceFrequency.WEEKLY && config.byDay.length === 0) {
      const dayIndex = startDate.getDay();
      setConfig((prev) => ({
        ...prev,
        byDay: [DAYS_OF_WEEK[dayIndex].short],
      }));
    }
  }, [config.frequency, config.byDay.length, startDate]);

  const updateConfig = (updates: Partial<RecurrenceConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  };

  const toggleDay = (day: string) => {
    setConfig((prev) => ({
      ...prev,
      byDay: prev.byDay.includes(day)
        ? prev.byDay.filter((d) => d !== day)
        : [...prev.byDay, day],
    }));
  };

  return (
    <div className={cn('space-y-4 p-4 bg-cream rounded-lg', className)}>
      {/* Frequency & Interval */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-navy font-medium">Repeat every</span>
        <input
          type="number"
          min={1}
          max={99}
          value={config.interval}
          onChange={(e) =>
            updateConfig({ interval: Math.max(1, parseInt(e.target.value, 10) || 1) })
          }
          className="w-16 px-3 py-1.5 bg-white border border-warm-gray/20 rounded-lg text-center text-sm focus:outline-none focus:border-sage"
        />
        <select
          value={config.frequency}
          onChange={(e) =>
            updateConfig({
              frequency: e.target.value as RecurrenceFrequency,
              byDay: [],
              byMonthDay: [],
            })
          }
          className="px-3 py-1.5 bg-white border border-warm-gray/20 rounded-lg text-sm focus:outline-none focus:border-sage"
        >
          {FREQUENCY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label.toLowerCase()}
              {config.interval > 1 ? 's' : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Weekly: Day Selection */}
      {config.frequency === RecurrenceFrequency.WEEKLY && (
        <div className="space-y-2">
          <span className="text-sm text-warm-gray">Repeat on</span>
          <div className="flex gap-1">
            {DAYS_OF_WEEK.map((day) => (
              <button
                key={day.short}
                type="button"
                onClick={() => toggleDay(day.short)}
                className={cn(
                  'w-9 h-9 rounded-full text-xs font-medium transition-colors',
                  config.byDay.includes(day.short)
                    ? 'bg-sage text-white'
                    : 'bg-white text-navy hover:bg-sage/10'
                )}
              >
                {day.short.charAt(0)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Monthly: Day of Month */}
      {config.frequency === RecurrenceFrequency.MONTHLY && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-warm-gray">On day</span>
          <input
            type="number"
            min={1}
            max={31}
            value={config.byMonthDay[0] || startDate.getDate()}
            onChange={(e) =>
              updateConfig({
                byMonthDay: [Math.min(31, Math.max(1, parseInt(e.target.value, 10) || 1))],
              })
            }
            className="w-16 px-3 py-1.5 bg-white border border-warm-gray/20 rounded-lg text-center text-sm focus:outline-none focus:border-sage"
          />
          <span className="text-sm text-warm-gray">of the month</span>
        </div>
      )}

      {/* End Condition */}
      <div className="space-y-3">
        <span className="text-sm text-navy font-medium">Ends</span>
        
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="endType"
              checked={config.endType === 'never'}
              onChange={() => updateConfig({ endType: 'never', endCount: undefined, endDate: undefined })}
              className="text-sage focus:ring-sage"
            />
            <span className="text-sm text-navy">Never</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="endType"
              checked={config.endType === 'after'}
              onChange={() => updateConfig({ endType: 'after', endCount: 10, endDate: undefined })}
              className="text-sage focus:ring-sage"
            />
            <span className="text-sm text-navy">After</span>
            {config.endType === 'after' && (
              <>
                <input
                  type="number"
                  min={1}
                  max={999}
                  value={config.endCount || 10}
                  onChange={(e) =>
                    updateConfig({ endCount: Math.max(1, parseInt(e.target.value, 10) || 1) })
                  }
                  className="w-16 px-2 py-1 bg-white border border-warm-gray/20 rounded text-center text-sm focus:outline-none focus:border-sage"
                />
                <span className="text-sm text-navy">occurrences</span>
              </>
            )}
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="endType"
              checked={config.endType === 'on'}
              onChange={() =>
                updateConfig({
                  endType: 'on',
                  endDate: format(addMonths(startDate, 3), 'yyyy-MM-dd'),
                  endCount: undefined,
                })
              }
              className="text-sage focus:ring-sage"
            />
            <span className="text-sm text-navy">On</span>
            {config.endType === 'on' && (
              <input
                type="date"
                value={config.endDate || format(addMonths(startDate, 3), 'yyyy-MM-dd')}
                min={format(startDate, 'yyyy-MM-dd')}
                onChange={(e) => updateConfig({ endDate: e.target.value })}
                className="px-2 py-1 bg-white border border-warm-gray/20 rounded text-sm focus:outline-none focus:border-sage"
              />
            )}
          </label>
        </div>
      </div>

      {/* Summary */}
      <div className="pt-3 border-t border-warm-gray/20">
        <div className="flex items-start gap-2 text-sm">
          <Repeat className="w-4 h-4 text-sage mt-0.5" />
          <span className="text-navy">{describeRRule(config, startDate)}</span>
        </div>
      </div>
    </div>
  );
}

export default RecurrenceEditor;
